import { useState } from "react";
import { styled } from "@mui/material/styles";
import { 
  Box, 
  Typography, 
  IconButton, 
  TextField,
  Avatar,
  Menu,
  MenuItem 
} from "@mui/material";
import { 
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Check as CheckIcon,
  Close as CloseIcon
} from "@mui/icons-material";

const CommentContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(1),
  backgroundColor: "#FAFBFC",
  marginBottom: theme.spacing(1.5),
  border: "1px solid #DFE1E6",
  "&:hover": {
    backgroundColor: "#F4F5F7",
  },
}));

const CommentContent = styled(Box)({
  flex: 1,
});

const CommentHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 4,
});

const CommentMeta = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 8,
});

// In CommentItem.jsx, update the RoleChip component:
const RoleChip = styled(Box)(({ role, theme }) => {
  // Define color mapping (same as frontend)
  const getRoleColor = (roleValue) => {
    const roleMap = {
      assignee: { bg: "#0052CC", color: "#FFFFFF" },
      reviewer: { bg: "#FFAB00", color: "#FFFFFF" },
      watcher: { bg: "#6554C0", color: "#FFFFFF" },
      productOwner: { bg: "#36B37E", color: "#FFFFFF" },
      reporter: { bg: "#DE350B", color: "#FFFFFF" },
      viewer: { bg: "#6B778C", color: "#FFFFFF" },
      observer: { bg: "#FF8B00", color: "#FFFFFF" },
    };
    
    // For custom roles or other roles
    return roleMap[role] || { bg: "#6B778C", color: "#FFFFFF" };
  };
  
  const colors = getRoleColor(role);
  
  return {
    backgroundColor: colors.bg,
    color: colors.color,
    fontSize: "0.65rem",
    fontWeight: 600,
    padding: "2px 8px",
    borderRadius: 10,
    textTransform: "capitalize",
  };
});


const CommentText = styled(Typography)(({ theme }) => ({
  fontSize: "0.85rem",
  lineHeight: 1.5,
  color: "#172B4D",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
}));

const CommentTimestamp = styled(Typography)(({ theme }) => ({
  fontSize: "0.75rem",
  color: "#6B778C",
  marginTop: theme.spacing(0.5),
}));

const CommentItem = ({ 
  comment, 
  currentEmployeeId, 
  onEdit, 
  onDelete 
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState(comment.comment_text);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);


const formatTime = (timestamp) => {
  try {
    const date = new Date(timestamp);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch (error) {
    return "Invalid date";
  }
};

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditClick = () => {
    setEditedText(comment.comment_text);
    setIsEditing(true);
    handleMenuClose();
  };

  const handleSaveEdit = () => {
    if (editedText.trim() && editedText !== comment.comment_text) {
      onEdit(comment.id, editedText);
    }
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditedText(comment.comment_text);
    setIsEditing(false);
  };

  const handleDeleteClick = () => {
    if (window.confirm("Are you sure you want to delete this comment?")) {
      onDelete(comment.id);
    }
    handleMenuClose();
  };

  const isCommentOwner = comment.employee_id === currentEmployeeId;

  return (
    <CommentContainer>
      {/* Avatar */}
      <Avatar
        sx={{
          width: 32,
          height: 32,
          fontSize: "0.75rem",
          bgcolor: getAvatarColor(comment.employee_name),
        }}
      >
        {getInitials(comment.employee_name)}
      </Avatar>

      <CommentContent>
        <CommentHeader>
          <CommentMeta>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, fontSize: "0.85rem" }}
            >
              {comment.employee_name}
            </Typography>
            <RoleChip role={comment.role}>
              {comment.role}
            </RoleChip>
          </CommentMeta>

          {isCommentOwner && !isEditing && (
            <IconButton
              size="small"
              onClick={handleMenuOpen}
              sx={{ width: 24, height: 24 }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          )}
        </CommentHeader>

        {isEditing ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <TextField
              multiline
              fullWidth
              rows={2}
              value={editedText}
              onChange={(e) => setEditedText(e.target.value)}
              autoFocus
              size="small"
              variant="outlined"
            />
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <IconButton
                size="small"
                onClick={handleSaveEdit}
                sx={{ width: 24, height: 24 }}
              >
                <CheckIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleCancelEdit}
                sx={{ width: 24, height: 24 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        ) : (
          <>
            <CommentText>{comment.comment_text}</CommentText>
            <CommentTimestamp>
              {formatTime(comment.created_at)}
              {comment.created_at !== comment.updated_at && " • Edited"}
            </CommentTimestamp>
          </>
        )}
      </CommentContent>

      {/* Menu for edit/delete */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        PaperProps={{
          sx: { width: 120 },
        }}
      >
        <MenuItem onClick={handleEditClick}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: "#DE350B" }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
    </CommentContainer>
  );
};

// Helper functions
const getInitials = (name) => {
  if (!name) return "?";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .substring(0, 2);
};

const getAvatarColor = (name) => {
  const colors = [
    "#0052CC", // Blue
    "#36B37E", // Green
    "#FF8B00", // Orange
    "#6554C0", // Purple
    "#DE350B", // Red
    "#00B8D9", // Cyan
    "#FF5630", // Red-Orange
  ];
  
  if (!name) return colors[0];
  
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  return colors[Math.abs(hash) % colors.length];
};

export default CommentItem;